import { toast } from "sonner";

const IconBluesky = () => (
  <svg viewBox="0 0 600 530" width="18" height="18" aria-hidden="true" fill="currentColor">
    <path d="M135 82c72 54 149 163 178 222 29-59 106-168 178-222 51-38 135-68 135 28 0 19-11 161-17 184-22 80-104 100-176 88 127 22 159 93 89 165-132 137-190-34-205-78-3-8-4-11-4-8 0-3-1 0-4 8-15 44-73 215-205 78-70-72-38-143 89-165-72 12-154-8-176-88-6-23-17-165-17-184 0-96 84-66 135-28z"/>
  </svg>
);

const IconFacebook = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
    <path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .593 23.407 0 22.675 0z"/>
  </svg>
);

const IconReddit = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.335-1.01 1.605a3.11 3.11 0 0 1 .042.52c0 2.657-3.088 4.807-6.9 4.807-3.812 0-6.9-2.15-6.9-4.807 0-.174.014-.347.042-.52-.575-.27-1.01-.889-1.01-1.605 0-.968.786-1.754 1.754-1.754.477 0 .898.182 1.207.49 1.207-.868 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.113-.732zM8.008 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm7.984 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-3.99 3.302c-1.302 0-2.401-.31-3.037-.786l-.286.286c.702.579 1.923.955 3.323.955s2.621-.376 3.323-.955l-.286-.286c-.636.475-1.735.786-3.037.786z"/>
  </svg>
);

function buildText(title) {
  // Collapse any line breaks in the title into single spaces for share targets
  // (Twitter/Bluesky/Reddit render titles on one line anyway).
  const clean = (title || "").replace(/\s*\n+\s*/g, " ").trim();
  return `${clean} — via What Do We Want? When Do We Want It?`;
}

export default function ShareBar({ title }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const singleLineTitle = (title || "").replace(/\s*\n+\s*/g, " ").trim();
  const text = buildText(title);
  const enc = encodeURIComponent;

  const links = [
    {
      key: "bluesky",
      label: "Bluesky",
      Icon: IconBluesky,
      color: "#0085FF",
      href: `https://bsky.app/intent/compose?text=${enc(`${text} ${url}`)}`,
    },
    {
      key: "reddit",
      label: "Reddit",
      Icon: IconReddit,
      color: "#FF4500",
      href: `https://reddit.com/submit?url=${enc(url)}&title=${enc(singleLineTitle)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: IconFacebook,
      color: "#1877F2",
      href: `https://www.facebook.com/sharer.php?u=${enc(url)}`,
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  return (
    <div className="mt-12 pt-6 border-t border-neutral-300" data-testid="share-bar">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-black mb-3">Share this!</div>
      <div className="flex flex-wrap items-center gap-2">
        {links.map((l) => {
          const handle = async (e) => {
            e.preventDefault();
            let win = null;
            try {
              win = window.open(l.href, "_blank", "noopener,noreferrer");
            } catch {
              /* COOP/popup blocked */
            }
            if (!win || win.closed || typeof win.closed === "undefined") {
              try {
                await navigator.clipboard.writeText(l.href);
                toast(`Popup blocked — ${l.label} share link copied to clipboard. Paste it into a new tab.`);
              } catch {
                toast.error("Popup blocked and clipboard access denied.");
              }
            }
          };
          return (
          <a
            key={l.key}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={handle}
            data-testid={`share-${l.key}`}
            aria-label={`Share on ${l.label}`}
            title={`Share on ${l.label}`}
            className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-neutral-300 hover:border-black hover:bg-black hover:text-white transition-colors"
          >
            <span style={{ color: l.color }} className="inline-flex">
              <l.Icon />
            </span>
            <span>{l.label}</span>
          </a>
          );
        })}
        <button
          type="button"
          onClick={copy}
          data-testid="share-copy-link"
          className="mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-neutral-300 hover:border-black hover:bg-black hover:text-white transition-colors"
        >
          Copy link
        </button>
      </div>
    </div>
  );
}
